import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import api, { formatApiErrorDetail } from "@/lib/api";
import { periodLabel, formatDate } from "@/lib/format";
import ClientPicker from "@/components/client/ClientPicker";
import { useAuth } from "@/context/AuthContext";
import DateInput from "@/components/ui/date-input";
import { CalendarIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TYPES = ["VAT", "Excise", "Accounting", "VAT Registration"];
const STATUSES = ["Not Started", "In Progress", "Waiting for Client Information", "Ready for Review", "Under Review", "Completed"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const MONTHS = [
  { v: 1, l: "January" }, { v: 2, l: "February" }, { v: 3, l: "March" }, { v: 4, l: "April" },
  { v: 5, l: "May" }, { v: 6, l: "June" }, { v: 7, l: "July" }, { v: 8, l: "August" },
  { v: 9, l: "September" }, { v: 10, l: "October" }, { v: 11, l: "November" }, { v: 12, l: "December" },
];
const now = new Date();
const YEARS = Array.from({ length: 6 }, (_, i) => now.getFullYear() - 1 + i);

function DatePick({ value, onChange, testid }) {
  return <DateInput value={value} onChange={onChange} testid={testid} />;
}

const empty = {
  compliance_type: "VAT",
  client_name: "",
  client_id: "",
  partner_name: "",
  responsible_person_name: "",
  supporting_team_ids: [],
  fees: 0,
  return_period: { kind: "custom", start_date: "", end_date: "" },
  deadline: "",
  status: "Not Started",
  priority: "Medium",
  notes: "",
  recurring: false,
  action_2: "Invoice Pending",
};

const PERIODIC_TYPES = ["VAT", "Excise", "Accounting", "VAT Registration"];
const ACTION2_OPTIONS = ["Invoice Pending", "Invoiced", "Partly Paid", "Paid"];

export default function ComplianceForm({ open, onClose, initial, users, onSaved, canEditAssignment = true }) {
  const { user } = useAuth();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const allowedTypes = (user?.allowed_compliance_types && user.allowed_compliance_types.length) ? user.allowed_compliance_types : TYPES;
  const typeOptions = TYPES.filter((t) => allowedTypes.includes(t));

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          compliance_type: initial.compliance_type,
          client_name: initial.client_name,
          client_id: initial.client_id || "",
          partner_name: initial.partner_name || initial.partner?.name || "",
          responsible_person_name: initial.responsible_person_name || initial.responsible_person?.name || "",
          supporting_team_ids: initial.supporting_team_ids || [],
          fees: initial.fees || 0,
          return_period: initial.return_period || { kind: "none" },
          deadline: (initial.deadline || "").slice(0, 10),
          status: initial.status,
          priority: initial.priority,
          notes: initial.notes || "",
          recurring: !!initial.recurring,
          action_2: initial.action_2 || "Invoice Pending",
        });
      } else {
        setForm({ ...empty, deadline: defaultDeadlineFromPeriod(empty.return_period) });
      }
    }
  }, [open, initial]);

  const supporting = useMemo(() => users, [users]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.deadline) {
      toast.error("Please fill client and deadline");
      return;
    }
    if (form.compliance_type === "VAT") {
      const rp = form.return_period || {};
      if (rp.kind === "monthly" && (!rp.month || !rp.year)) return toast.error("Select VAT month & year");
      if (rp.kind === "quarterly" && (!rp.quarter || !rp.year)) return toast.error("Select VAT quarter & year");
      if (rp.kind === "custom" && (!rp.start_date || !rp.end_date)) return toast.error("Select VAT custom period range");
    }
    setSaving(true);
    try {
      const payload = { ...form, fees: Number(form.fees) || 0 };
      if (!PERIODIC_TYPES.includes(payload.compliance_type)) {
        payload.return_period = { kind: "none" };
      }
      if (initial) {
        await api.patch(`/compliances/${initial.id}`, payload);
        toast.success("Compliance updated");
      } else {
        await api.post("/compliances", payload);
        toast.success("Compliance created");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const rp = form.return_period || { kind: "none" };
  const showVatPeriod = PERIODIC_TYPES.includes(form.compliance_type);

  const auditMeta = initial ? (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-[11px] text-slate-600 space-y-0.5" data-testid="compliance-audit-meta">
      {initial.created_by_name && (
        <div>Created by <span className="font-medium text-slate-800">{initial.created_by_name}</span>{initial.created_at ? " on " + initial.created_at.slice(0, 10) : ""}</div>
      )}
      {initial.updated_by_name && (
        <div>Last updated by <span className="font-medium text-slate-800">{initial.updated_by_name}</span>{initial.updated_at ? " on " + initial.updated_at.slice(0, 10) : ""}</div>
      )}
    </div>
  ) : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display" data-testid="compliance-form-title">
            {initial ? "Edit Compliance" : "New Compliance"}
          </DialogTitle>
          <DialogDescription>
            {initial ? "Update record details, status or team." : "Create a new compliance record."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4" data-testid="compliance-form">
          {auditMeta}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Compliance Type</Label>
              <Select value={form.compliance_type} onValueChange={(v) => set({ compliance_type: v })} disabled={!canEditAssignment}>
                <SelectTrigger className="mt-1.5" data-testid="form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{typeOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Client</Label>
              <div className="mt-1.5">
                <ClientPicker
                  value={form.client_id ? { id: form.client_id, name: form.client_name } : null}
                  onChange={(c) => set({ client_id: c?.id || "", client_name: c?.name || "" })}
                  disabled={!canEditAssignment}
                  testid="form-client-picker"
                />
              </div>
            </div>
          </div>

          {showVatPeriod && (
            <div className="rounded-lg border border-slate-200 p-4 bg-slate-50/60 space-y-3">
              <div className="text-xs uppercase tracking-widest text-slate-600 font-semibold">Return Period</div>
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
                <div className="sm:col-span-1">
                  <Label className="text-xs">Frequency</Label>
                  <Select value={rp.kind} onValueChange={(k) => set({ return_period: { ...rp, kind: k }, deadline: defaultDeadlineFromPeriod({ ...rp, kind: k }) })} disabled={!canEditAssignment}>
                    <SelectTrigger className="mt-1.5" data-testid="form-vat-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {rp.kind === "monthly" && (
                  <>
                    <div>
                      <Label className="text-xs">Month</Label>
                      <Select value={String(rp.month || "")} onValueChange={(v) => set({ return_period: { ...rp, month: Number(v) }, deadline: defaultDeadlineFromPeriod({ ...rp, month: Number(v) }) })} disabled={!canEditAssignment}>
                        <SelectTrigger className="mt-1.5" data-testid="form-vat-month"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{MONTHS.map((m) => <SelectItem key={m.v} value={String(m.v)}>{m.l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Year</Label>
                      <Select value={String(rp.year || "")} onValueChange={(v) => set({ return_period: { ...rp, year: Number(v) }, deadline: defaultDeadlineFromPeriod({ ...rp, year: Number(v) }) })} disabled={!canEditAssignment}>
                        <SelectTrigger className="mt-1.5" data-testid="form-vat-year"><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {rp.kind === "quarterly" && (
                  <>
                    <div>
                      <Label className="text-xs">Quarter</Label>
                      <Select value={String(rp.quarter || "")} onValueChange={(v) => set({ return_period: { ...rp, quarter: Number(v) }, deadline: defaultDeadlineFromPeriod({ ...rp, quarter: Number(v) }) })} disabled={!canEditAssignment}>
                        <SelectTrigger className="mt-1.5" data-testid="form-vat-quarter"><SelectValue placeholder="Q?" /></SelectTrigger>
                        <SelectContent>{[1,2,3,4].map((q) => <SelectItem key={q} value={String(q)}>Q{q}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-xs">Year</Label>
                      <Select value={String(rp.year || "")} onValueChange={(v) => set({ return_period: { ...rp, year: Number(v) }, deadline: defaultDeadlineFromPeriod({ ...rp, year: Number(v) }) })} disabled={!canEditAssignment}>
                        <SelectTrigger className="mt-1.5" data-testid="form-vat-year"><SelectValue placeholder="Year" /></SelectTrigger>
                        <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                {rp.kind === "custom" && (
                  <>
                    <div>
                      <Label className="text-xs">Start</Label>
                      <div className="mt-1.5"><DatePick value={rp.start_date} onChange={(d) => set({ return_period: { ...rp, start_date: d } })} testid="form-vat-start" /></div>
                    </div>
                    <div>
                      <Label className="text-xs">End</Label>
                      <div className="mt-1.5"><DatePick value={rp.end_date} onChange={(d) => set({ return_period: { ...rp, end_date: d } })} testid="form-vat-end" /></div>
                    </div>
                  </>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                <div className="rounded-md bg-white border border-slate-200 px-3 py-2">
                  <div className="text-slate-500 uppercase text-[10px] tracking-widest">Period Start</div>
                  <div className="font-mono-num font-medium">{previewPeriodDates(rp).start ? formatDate(previewPeriodDates(rp).start) : "—"}</div>
                </div>
                <div className="rounded-md bg-white border border-slate-200 px-3 py-2">
                  <div className="text-slate-500 uppercase text-[10px] tracking-widest">Period End</div>
                  <div className="font-mono-num font-medium">{previewPeriodDates(rp).end ? formatDate(previewPeriodDates(rp).end) : "—"}</div>
                </div>
                <div className="rounded-md bg-white border border-slate-200 px-3 py-2">
                  <div className="text-slate-500 uppercase text-[10px] tracking-widest">Label</div>
                  <div className="font-mono-num font-medium">{periodLabel(computePreview(rp))}</div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 mt-1">
                <Checkbox checked={!!form.recurring} onCheckedChange={(v) => set({ recurring: !!v })} data-testid="form-recurring" />
                Mark this as recurring (enables "Next Period" action)
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Deadline</Label>
              <div className="mt-1.5"><DatePick value={form.deadline} onChange={(d) => set({ deadline: d })} testid="form-deadline" /></div>
              <div className="text-[11px] text-slate-500 mt-1">You can edit the deadline manually.</div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Fees (AED)</Label>
              <Input type="number" min="0" step="1" value={form.fees}
                onChange={(e) => set({ fees: e.target.value })}
                placeholder="0"
                className="mt-1.5 font-mono-num" disabled={!canEditAssignment} data-testid="form-fees" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Partner <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span></Label>
              <Input
                value={form.partner_name}
                onChange={(e) => set({ partner_name: e.target.value })}
                placeholder="e.g. Alice Partner"
                className="mt-1.5"
                disabled={!canEditAssignment}
                data-testid="form-partner"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Responsible Person <span className="text-slate-400 normal-case tracking-normal font-normal">(optional)</span></Label>
              <Input
                value={form.responsible_person_name}
                onChange={(e) => set({ responsible_person_name: e.target.value })}
                placeholder="e.g. Riya"
                className="mt-1.5"
                disabled={!canEditAssignment}
                data-testid="form-responsible"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600">Supporting Team (optional)</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {supporting.map((u) => {
                const checked = form.supporting_team_ids.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    disabled={!canEditAssignment}
                    onClick={() =>
                      set({
                        supporting_team_ids: checked
                          ? form.supporting_team_ids.filter((x) => x !== u.id)
                          : [...form.supporting_team_ids, u.id],
                      })
                    }
                    className={cn(
                      "text-xs px-2.5 py-1.5 rounded-full border transition-colors",
                      checked ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 text-slate-700 hover:border-slate-400"
                    )}
                    data-testid={`form-supporting-${u.id}`}
                  >
                    {u.name}
                  </button>
                );
              })}
              {!supporting.length && <div className="text-xs text-slate-500">No other team members available.</div>}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Status</Label>
              <Select value={form.status} onValueChange={(v) => set({ status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Priority</Label>
              <Select value={form.priority} onValueChange={(v) => set({ priority: v })} disabled={!canEditAssignment}>
                <SelectTrigger className="mt-1.5" data-testid="form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600">Invoice Status</Label>
            <Select value={form.action_2} onValueChange={(v) => set({ action_2: v })}>
              <SelectTrigger className="mt-1.5" data-testid="form-action-2"><SelectValue /></SelectTrigger>
              <SelectContent>{ACTION2_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set({ notes: e.target.value })} rows={3} className="mt-1.5" data-testid="form-notes" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} data-testid="form-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="form-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {initial ? "Save changes" : "Create compliance"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function computePreview(rp) {
  const p = previewPeriodDates(rp);
  return { ...rp, start_date: p.start, end_date: p.end };
}

function previewPeriodDates(rp) {
  if (!rp) return {};
  if (rp.kind === "monthly" && rp.month && rp.year) {
    const start = new Date(Date.UTC(rp.year, rp.month - 1, 1)).toISOString().slice(0, 10);
    const end = new Date(Date.UTC(rp.year, rp.month, 0)).toISOString().slice(0, 10);
    return { start, end };
  }
  if (rp.kind === "quarterly" && rp.quarter && rp.year) {
    const sm = (rp.quarter - 1) * 3;
    const start = new Date(Date.UTC(rp.year, sm, 1)).toISOString().slice(0, 10);
    const end = new Date(Date.UTC(rp.year, sm + 3, 0)).toISOString().slice(0, 10);
    return { start, end };
  }
  if (rp.kind === "custom") return { start: rp.start_date, end: rp.end_date };
  return {};
}

function defaultDeadlineFromPeriod(rp) {
  const p = previewPeriodDates(rp);
  if (!p.end) return "";
  const end = new Date(p.end + "T00:00:00Z");
  end.setUTCDate(end.getUTCDate() + 28);
  return end.toISOString().slice(0, 10);
}
